import React, { useState, useEffect, useMemo } from 'react';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { X, UserPlus, Search, Check, Users } from 'lucide-react';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { engagementApi } from '@/lib/api/engagementApi';
import { OnboardingProgram } from '@/lib/models/onboarding';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import type { Employee } from '@/lib/models/engagement';
import { useEmployer } from '@/contexts/EmployerContext';
import { ActionButton } from '@/components/custom-ui/action-button';
import { cn } from '@/lib/utils';

interface AssignOnboardingModalProps {
    program: OnboardingProgram;
    onClose: () => void;
    onSuccess: () => void;
}

const fullName = (e: Employee): string => {
    const name = [e.first_name, e.last_name].filter(Boolean).join(' ').trim();
    return name || e.email || 'Okänd användare';
};

const initials = (e: Employee): string => {
    const a = e.first_name?.[0] ?? '';
    const b = e.last_name?.[0] ?? '';
    return (a + b).toUpperCase() || (e.email?.[0] ?? '?').toUpperCase();
};

const matchesSearch = (e: Employee, term: string): boolean => {
    if (!term) return true;
    const t = term.toLowerCase();
    return (
        (e.first_name ?? '').toLowerCase().includes(t) ||
        (e.last_name ?? '').toLowerCase().includes(t) ||
        (e.email ?? '').toLowerCase().includes(t) ||
        (e.unit_name ?? '').toLowerCase().includes(t)
    );
};

const AssignOnboardingModal: React.FC<AssignOnboardingModalProps> = ({ program, onClose, onSuccess }) => {
    const { employerDetails } = useEmployer();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [assignedUserIds, setAssignedUserIds] = useState<Set<string>>(new Set());
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const orgId = employerDetails?.organisation?.id;
        if (!orgId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        Promise.all([
            engagementApi.listOrganisationEmployees(orgId),
            onboardingApi.getProgramEmployees(program.id),
        ])
            .then(([list, assigned]) => {
                // Sort alphabetically by name for predictable scanning.
                const sorted = [...list].sort((a, b) => fullName(a).localeCompare(fullName(b), 'sv'));
                setEmployees(sorted);
                setAssignedUserIds(new Set((assigned.employees ?? []).map((e) => e.user_id)));
            })
            .catch(() => setEmployees([]))
            .finally(() => setIsLoading(false));
    }, [employerDetails?.organisation?.id, program.id]);

    const filtered = useMemo(
        () => employees.filter((e) => matchesSearch(e, searchTerm)),
        [employees, searchTerm]
    );

    // Only not-yet-assigned employees are selectable.
    const assignable = useMemo(
        () => filtered.filter((e) => !assignedUserIds.has(e.user_id)),
        [filtered, assignedUserIds]
    );
    const visibleSelectedCount = useMemo(
        () => assignable.filter((e) => selected.has(e.user_id)).length,
        [assignable, selected]
    );
    const allVisibleSelected = assignable.length > 0 && visibleSelectedCount === assignable.length;

    const toggleOne = (userId: string) => {
        if (assignedUserIds.has(userId)) return; // already assigned — not selectable
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
        setError(null);
    };

    const toggleAllVisible = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                assignable.forEach((e) => next.delete(e.user_id));
            } else {
                assignable.forEach((e) => next.add(e.user_id));
            }
            return next;
        });
        setError(null);
    };

    const handleSubmit = async () => {
        if (selected.size === 0) {
            setError('Välj minst en medarbetare');
            return;
        }
        setIsSubmitting(true);
        setError(null);

        const ids = Array.from(selected);
        const results = await Promise.allSettled(
            ids.map((user_id) =>
                onboardingApi.assignOnboarding({ user_id, program_id: program.id })
            )
        );

        const failures = results.filter((r) => r.status === 'rejected').length;
        const successes = results.length - failures;

        setIsSubmitting(false);

        if (failures === 0) {
            onSuccess();
            return;
        }

        // Partial failure — keep modal open, surface a brief summary.
        // Common cause: one or more medarbetare already assigned to this program.
        if (successes > 0) {
            setError(`${successes} av ${results.length} tilldelades. ${failures} kunde inte tilldelas (kanske redan tilldelade).`);
            // Refresh parent so successes show up.
            onSuccess();
        } else {
            setError('Tilldelningen misslyckades. Medarbetarna kan redan vara tilldelade detta program.');
        }
    };

    const selectedCount = selected.size;

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-lg">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Tilldela Onboarding</h2>
                        <p className="text-sm text-gray-500">{program.name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Stäng">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Search + select-all */}
                <div className="px-6 pt-5 pb-3 space-y-3 flex-shrink-0">
                    <label className="block text-sm font-medium text-gray-700">Välj medarbetare</label>
                    <div className="relative">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Sök på namn, e-post eller enhet..."
                            className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent"
                        />
                    </div>

                    {employees.length > 0 && (
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500">
                                {selectedCount > 0 ? `${selectedCount} valda` : 'Inga valda'}
                                {searchTerm && ` · ${filtered.length} av ${employees.length}`}
                            </span>
                            {filtered.length > 0 && (
                                <button
                                    type="button"
                                    onClick={toggleAllVisible}
                                    className="text-[#1A4D4F] font-medium hover:underline"
                                >
                                    {allVisibleSelected ? 'Avmarkera alla synliga' : 'Välj alla synliga'}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Body — scrollable list */}
                <div className="flex-1 overflow-y-auto px-6 pb-3 min-h-[120px]">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-10 text-sm text-gray-500">
                            Laddar medarbetare...
                        </div>
                    ) : employees.length === 0 ? (
                        <div className="text-center py-10 px-4">
                            <div className="w-12 h-12 mx-auto mb-3 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                                <Users className="w-6 h-6 text-[#1A4D4F]" />
                            </div>
                            <p className="text-sm text-gray-600">
                                Inga medarbetare hittades. Anställ kandidater via en jobbansökan eller inbjudan först.
                            </p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-10 text-sm text-gray-500">
                            Inga medarbetare matchar "{searchTerm}".
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {filtered.map((emp) => {
                                const isAssigned = assignedUserIds.has(emp.user_id);
                                const isSelected = selected.has(emp.user_id);
                                return (
                                    <li key={emp.user_id}>
                                        <button
                                            type="button"
                                            onClick={() => toggleOne(emp.user_id)}
                                            disabled={isAssigned}
                                            className={cn(
                                                'w-full flex items-center gap-3 px-2 py-2.5 rounded-lg transition-colors text-left',
                                                isAssigned
                                                    ? 'opacity-60 cursor-default'
                                                    : isSelected ? 'bg-[#A4C3B2]/30' : 'hover:bg-gray-50'
                                            )}
                                            aria-pressed={isSelected}
                                        >
                                            {/* Custom checkbox */}
                                            <div
                                                className={cn(
                                                    'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                                                    isAssigned
                                                        ? 'bg-gray-200 border-gray-200'
                                                        : isSelected
                                                            ? 'bg-[#1A4D4F] border-[#1A4D4F]'
                                                            : 'border-gray-300 bg-white'
                                                )}
                                            >
                                                {(isSelected || isAssigned) && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                            </div>

                                            {/* Avatar */}
                                            {emp.picture_url ? (
                                                <img
                                                    src={resolveMediaUrl(emp.picture_url)}
                                                    alt=""
                                                    className="w-9 h-9 rounded-full object-cover bg-gray-100 flex-shrink-0"
                                                />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-[#A4C3B2] flex items-center justify-center text-xs font-semibold text-[#1A4D4F] flex-shrink-0">
                                                    {initials(emp)}
                                                </div>
                                            )}

                                            {/* Name + meta */}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                                                    <span className="truncate">{fullName(emp)}</span>
                                                    {isAssigned && (
                                                        <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#1A4D4F] bg-[#A4C3B2]/40 px-1.5 py-0.5 rounded">
                                                            Tilldelad
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    {emp.email}
                                                    {emp.unit_name && <span> · {emp.unit_name}</span>}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Error / status */}
                {error && (
                    <div className="px-6 pb-3 flex-shrink-0">
                        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">{error}</p>
                    </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 p-6 border-t border-gray-100 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl"
                        disabled={isSubmitting}
                    >
                        Avbryt
                    </button>
                    <ActionButton
                        icon={UserPlus}
                        onClick={handleSubmit}
                        disabled={isSubmitting || selectedCount === 0}
                    >
                        {isSubmitting
                            ? 'Tilldelar...'
                            : selectedCount > 0
                                ? `Tilldela (${selectedCount})`
                                : 'Tilldela'}
                    </ActionButton>
                </div>
        </IslandSlideOver>
    );
};

export default AssignOnboardingModal;
